import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon } from 'lucide-react';
type WelcomeOnboardingProps = {
  onComplete: () => void;
};
export const WelcomeOnboarding: React.FC<WelcomeOnboardingProps> = ({
  onComplete
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [{
    title: 'Discover Your True Career Path',
    subtitle: 'Where Science Meets Destiny',
    description: 'We blend astrology, science, and psychology to help you find the career that aligns with your unique traits and potential.',
    icon: '✨',
    color: 'from-[#7D56F4] to-[#19D3DA]'
  }, {
    title: 'Your Career Match is Unique to YOU',
    subtitle: 'Personalized Career Insights',
    description: 'No generic advice here. Your Career Genome is as unique as your fingerprint, based on multiple dimensions of who you are.',
    icon: '🧬',
    color: 'from-[#FF8F5E] to-[#FFD700]'
  }, {
    title: 'Fun, Fast, and Insightful!',
    subtitle: 'Enjoy the Journey',
    description: 'Earn XP, unlock badges, and track your progress as you discover your ideal career path through engaging activities.',
    icon: '🚀',
    color: 'from-[#19D3DA] to-[#7D56F4]'
  }];
  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };
  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };
  const isLastSlide = currentSlide === slides.length - 1;
  return <div className="min-h-screen w-full bg-gradient-to-b from-[#0A1128] to-[#1C2541] text-white overflow-hidden flex flex-col" data-id="element-1346">
      {/* Stars background */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-1347">
        <div className="absolute top-0 left-0 w-full h-full opacity-30" data-id="element-1348">
          {[...Array(100)].map((_, i) => <div key={i} className="absolute rounded-full bg-white" style={{
          width: Math.random() * 3 + 1 + 'px',
          height: Math.random() * 3 + 1 + 'px',
          top: Math.random() * 100 + '%',
          left: Math.random() * 100 + '%',
          opacity: Math.random() * 0.8 + 0.2,
          animation: `twinkle ${Math.random() * 5 + 5}s infinite`
        }} data-id="element-1349" />)}
        </div>
      </div>
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-12" data-id="element-1350">
        {/* Progress indicator */}
        <div className="absolute top-6 left-1/2 transform -translate-x-1/2 flex space-x-2" data-id="element-1351">
          {slides.map((_, index) => <div key={index} className={`w-2 h-2 rounded-full ${index === currentSlide ? 'bg-[#7D56F4]' : 'bg-[#3A506B]'}`} data-id="element-1352" />)}
        </div>
        <AnimatePresence mode="wait" data-id="element-1353">
          <motion.div key={currentSlide} initial={{
          opacity: 0,
          x: 100
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -100
        }} transition={{
          duration: 0.5
        }} className="max-w-md w-full text-center" data-id="element-1354">
            <div className={`mb-8 mx-auto w-24 h-24 rounded-full flex items-center justify-center text-4xl bg-gradient-to-r ${slides[currentSlide].color}`} data-id="element-1355">
              {slides[currentSlide].icon}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-1356">
              {slides[currentSlide].title}
            </h1>
            <h2 className="text-xl text-[#B8C0E0] mb-6" data-id="element-1357">
              {slides[currentSlide].subtitle}
            </h2>
            <p className="text-[#E1E6FF] mb-10" data-id="element-1358">
              {slides[currentSlide].description}
            </p>
            <div className="flex justify-between items-center" data-id="element-1359">
              <button onClick={prevSlide} className={`p-3 rounded-full ${currentSlide === 0 ? 'text-[#3A506B] cursor-not-allowed' : 'text-white hover:bg-[#3A506B]'}`} disabled={currentSlide === 0} data-id="element-1360">
                <ArrowLeftIcon size={20} data-id="element-1361" />
              </button>
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={nextSlide} className={`py-3 px-8 rounded-full font-medium flex items-center ${isLastSlide ? 'bg-gradient-to-r from-[#7D56F4] to-[#19D3DA]' : 'bg-[#3A506B]'}`} data-id="element-1362">
                {isLastSlide ? <>
                    <span className="mr-2" data-id="element-1363">Let's Begin</span>
                    <motion.span animate={{
                  scale: [1, 1.2, 1],
                  opacity: [1, 0.8, 1]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity
                }} data-id="element-1364">
                      ✨
                    </motion.span>
                  </> : <>
                    <span data-id="element-1365">Next</span>
                    <ArrowRightIcon size={18} className="ml-2" data-id="element-1366" />
                  </>}
              </motion.button>
              <div className="w-12" data-id="element-1367">{/* Empty div for spacing */}</div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
};